import React from "react";
import { Link } from "react-router-dom";
import { Bird, ArrowLeft } from "lucide-react";

const terms = [
  {
    title: "Be kind, first and always.",
    body: "This space exists for warmth and quiet presence. Speak to others the way you would want someone to speak to you on a hard day. No insults, no name-calling, no contempt.",
  },
  {
    title: "Hold what is shared with care.",
    body: "What members share inside the community stays inside the community. Do not screenshot, forward, repost, or quote anyone outside Never Alone in Singapore without their clear permission.",
  },
  {
    title: "Listen before you fix.",
    body: 'You are not obligated to solve anyone\u2019s life. Most people here are not asking for advice \u2014 they\u2019re asking to be heard. Lead with listening; only offer suggestions when invited.',
  },
  {
    title: "No selling, recruiting, or promoting.",
    body: "This is not a marketplace. Please do not pitch products, courses, services, MLMs, religious or political recruitment, investment schemes, or affiliate links. Sharing your own creative work is okay only when it is contextually relevant and never pushy.",
  },
  {
    title: "Keep it safe for everyone.",
    body: "Hate speech, harassment, doxxing, threats, sexual content involving minors, or content that targets a person\u2019s race, religion, language, gender, sexuality, ability, or nationality is not allowed. Singapore law applies here, and so does basic human respect.",
  },
  {
    title: "Crisis support is bigger than us.",
    body: "We are a peer community, not a crisis service. If you or someone here is in danger or in crisis, please contact a Singapore helpline (e.g. Samaritans of Singapore, 1767) or emergency services (995). Members may gently signpost help; that is care, not rejection.",
  },
  {
    title: "Your data, your control.",
    body: "When you sign up, you share your first name and email so we can welcome you and send occasional community updates. We will not sell your data. You can ask us to delete your account and all associated personal data at any time by replying to any community email.",
  },
  {
    title: "One person, one account; please use real ways to be reached.",
    body: "Use a real email you actually check. Multiple accounts, impersonation, bots, or scraping the community are not allowed. We may remove accounts that look automated or that exist only to disrupt.",
  },
  {
    title: "Moderation is on the side of the quiet.",
    body: "Moderators may edit, hide, or remove posts and may pause or remove accounts that repeatedly break these terms. Decisions favour the people who came here for calm. You can always write to us if you feel a call was unfair.",
  },
  {
    title: "These terms can grow with us.",
    body: "We may update these terms as the community grows. If something material changes, we\u2019ll let members know. Continuing to use Never Alone in Singapore after an update means you accept the updated terms.",
  },
];

const Terms = () => {
  return (
    <div className="min-h-screen bg-[#f6f9fc]">
      <header className="bg-[#354556] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/10 border border-white/20">
              <Bird className="h-5 w-5 text-[#cfe7f8]" />
            </span>
            <span className="ht-display text-lg">Never Alone SG</span>
          </Link>
          <Link
            to="/"
            className="ht-btn inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 px-4 py-2 text-sm"
          >
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <p className="text-[#4a90c2] font-semibold uppercase tracking-[0.18em] text-xs ht-body">
          Community Terms
        </p>
        <h1 className="mt-3 ht-display text-3xl sm:text-4xl text-[#2c3e50] leading-[1.15]">
          Ten quiet promises for Never Alone in Singapore.
        </h1>
        <p className="mt-5 ht-body text-[#5a6b7d] text-base sm:text-lg leading-relaxed">
          These are the agreements that hold this space steady. They&apos;re
          short on purpose. Read them once, and you&apos;ll know how we try to
          show up for one another here.
        </p>

        <ol className="mt-10 space-y-7">
          {terms.map((t, i) => (
            <li
              key={t.title}
              className="ht-card rounded-2xl bg-white border border-[#e6eef5] p-6 sm:p-7 shadow-sm"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#eaf4fb] text-[#3d6486] font-semibold ht-display">
                  {i + 1}
                </span>
                <div>
                  <h2 className="ht-display text-lg sm:text-xl text-[#2c3e50] leading-snug">
                    {t.title}
                  </h2>
                  <p className="mt-2 ht-body text-[#5a6b7d] leading-relaxed">
                    {t.body}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 rounded-2xl bg-[#eaf4fb] border border-[#cfe7f8] p-6 sm:p-7">
          <p className="ht-display text-lg text-[#2c3e50]">Get in touch</p>
          <p className="mt-2 ht-body text-[#2c3e50]">
            Questions about these terms, or something that happened in the
            community? A real person reads every message.
          </p>
          <ul className="mt-4 space-y-2 ht-body text-[#2c3e50]">
            <li>
              <span className="font-semibold">Email:</span>{" "}
              <a
                href="mailto:drkev@drkevinkwok.org"
                className="text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
              >
                drkev@drkevinkwok.org
              </a>
            </li>
            <li>
              <span className="font-semibold">Phone / WhatsApp:</span>{" "}
              <a
                href="tel:+6583669817"
                className="text-[#3d6486] hover:text-[#2c3e50] underline-offset-4 hover:underline"
              >
                +65 8366 9817
              </a>
            </li>
            <li>
              <span className="font-semibold">Address:</span> 16 Raffles Quay
              #14-05, Singapore 048581
            </li>
          </ul>
        </div>

        <p className="mt-8 text-xs text-[#7e8a96]">
          Last updated: June 2026. These community terms are written for clarity,
          not as legal advice. Singapore law applies.
        </p>
      </main>
    </div>
  );
};

export default Terms;
